import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDebouncedValue} from 'webapp-lib/utils';

import {InternalHoursCategory} from '@lexian/api/time-tracking-api';
import {searchAssignableCasus} from '_/store/searchCasus';
import {
  toInternalCostCenterDisplayText,
  toInternalCostCenterId,
} from '_/utilities/costCenter';
import {EFAutocomplete, EFAutocompleteProps, useWatch} from '_/utilities/form';
import {PaginatedItemLoader} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PaginationDirection,
  PaginationMode,
} from '_/utilities/types';

import {COST_CENTER_CATEGORIES, CostCenterCategoryNames} from './utils';

type CostCenterAutocompleteProps = Omit<
  EFAutocompleteProps,
  | 'options'
  | 'hasMoreAfter'
  | 'hasMoreBefore'
  | 'onLoadMoreBefore'
  | 'onLoadMoreAfter'
> & {
  internalHoursCategories: InternalHoursCategory[];
};

const UCostCenterAutocomplete = ({
  internalHoursCategories,
  control,
  name,
  defaultValue,
  ...otherProps
}: CostCenterAutocompleteProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const [casusList] = useState(
    () => new PaginatedItemLoader(searchAssignableCasus)
  );
  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');

  const selectedCasusId = useWatch({
    control,
    name,
    defaultValue,
  });

  const internalHoursOptions = internalHoursCategories
    .filter((internal) => internal.id !== undefined)
    .map(({id, name: internalCategoryName}) => ({
      value: toInternalCostCenterId(id),
      displayText: toInternalCostCenterDisplayText(internalCategoryName),
      categoryName: CostCenterCategoryNames.INTERNAL,
    }))
    .filter(
      (option) =>
        !debounceSearchQuery ||
        option.displayText
          .toLowerCase()
          .includes(debounceSearchQuery.toLowerCase())
    );
  const casusOptions = (casusList.loadable.data || []).map(({item}) => ({
    displayText: item.displayNameWithClient,
    value: item.id,
    subtext: t('casusNumber', {casusNumber: item.casusNumber}),
    categoryName: item.isFavorite
      ? CostCenterCategoryNames.FAVORITES
      : CostCenterCategoryNames.OTHERS,
  }));

  const costCenterOptions = [
    ...(casusList.loadable.hasMoreBefore ? [] : internalHoursOptions),
    ...casusOptions,
  ];

  const onLoadMoreCasusBefore = useCallback(async () => {
    await casusList.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
      {searchQuery}
    );
  }, [casusList, searchQuery]);

  const onLoadMoreCasusAfter = useCallback(async () => {
    await casusList.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
      {searchQuery}
    );
  }, [casusList, searchQuery]);

  // handle fetching logic
  useEffect(() => {
    if (selectedCasusId && !debounceSearchQuery) {
      casusList.fetch(
        {
          mode: PaginationMode.Navigate,
          key: [selectedCasusId],
        },
        {searchQuery: ''}
      );
    } else {
      casusList.fetch(defaultPaginationArguments, {
        searchQuery: debounceSearchQuery,
      });
    }
  }, [debounceSearchQuery, selectedCasusId, casusList]);

  return (
    <EFAutocomplete
      {...otherProps}
      categories={COST_CENTER_CATEGORIES}
      control={control}
      defaultValue={defaultValue}
      hasMoreAfter={casusList.loadable.hasMoreAfter}
      hasMoreBefore={casusList.loadable.hasMoreBefore}
      name={name}
      options={costCenterOptions}
      noOptionFiltering
      onLoadMoreAfter={onLoadMoreCasusAfter}
      onLoadMoreBefore={onLoadMoreCasusBefore}
      onSearchQueryChange={setSearchQuery}
    />
  );
};

export const CostCenterAutocomplete = observer(UCostCenterAutocomplete);
